import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FaBars, FaChevronDown, FaChevronRight, FaPlus } from 'react-icons/fa6';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import { useU3204ConversationHandlers } from '../hooks/useU3204ConversationHandlers';
import { useU3204MissionDeepLink } from '../hooks/useU3204MissionDeepLink';
import { u3204NormalizeMissionRef, type U3204ConversationSearchRow } from '../U3204Service';
import {
  groupConversationsForSidebar,
  type U3204ConversationSidebarGroupKey,
} from '../utils/u3204GroupConversationsForSidebar';
import { readLastMissionId, writeLastMissionId } from '../utils/u3204LastMissionStorage';
import U3204ConversationDeleteDlg from './U3204ConversationDeleteDlg';
import U3204ConversationFilterInput from './U3204ConversationFilterInput';
import U3204ConversationGroupSection from './U3204ConversationGroupSection';
import U3204ConversationRenameDlg from './U3204ConversationRenameDlg';
import U3204MissionSelector from './U3204MissionSelector';

interface U3204ConversationSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const U3204ConversationSidebar: React.FC<U3204ConversationSidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const { t } = useTranslation(NS);
  const [searchParams] = useSearchParams();
  const {
    conversations,
    archivedConversations,
    activeConversationId,
    selectedMissionId,
    setSelectedMissionId,
    setActiveMission,
    isNewChat,
    conversationFilterText,
    setConversationFilterText,
    loadingConversations,
    loadingArchivedConversations,
    archivesExpanded,
    setArchivesExpanded,
    initLU,
    accessibleMissions,
    loadingAccessibleMissions,
  } = useU3204Context();
  const missions = accessibleMissions;
  const hasAccessibleMissions = missions.length > 0;
  const {
    loadConversations,
    loadArchivedConversations,
    loadConversation,
    createConversation,
    renameConversation,
    archiveConversation,
    unarchiveConversation,
    deleteConversation,
    pinConversation,
    unpinConversation,
    reorderPinnedConversations,
  } = useU3204ConversationHandlers();

  const [renameTarget, setRenameTarget] = useState<U3204ConversationSearchRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<U3204ConversationSearchRow | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Partial<Record<U3204ConversationSidebarGroupKey, boolean>>>({});
  const [archivedListExpanded, setArchivedListExpanded] = useState(false);
  const [missionsInitialized, setMissionsInitialized] = useState(false);
  const [reorderingPinned, setReorderingPinned] = useState(false);

  useEffect(() => {
    if (loadingAccessibleMissions) return;

    // Deep-link ?mission= prioritaire sur lastMission (BL-0201)
    if (searchParams.get('mission')) {
      setMissionsInitialized(true);
      return;
    }

    const stored = readLastMissionId();
    if (stored && missions.some((mission) => mission.id === stored)) {
      setSelectedMissionId(stored);
    } else {
      if (stored) {
        writeLastMissionId(null);
      }
      setSelectedMissionId(null);
    }
    setMissionsInitialized(true);
  }, [loadingAccessibleMissions, missions, searchParams, setSelectedMissionId]);

  useU3204MissionDeepLink(missionsInitialized);

  useEffect(() => {
    if (!missionsInitialized) return;

    const timer = window.setTimeout(() => {
      void loadConversations();
      void loadArchivedConversations();
    }, conversationFilterText.trim() ? 300 : 0);

    return () => window.clearTimeout(timer);
  }, [
    conversationFilterText,
    loadArchivedConversations,
    loadConversations,
    missionsInitialized,
    selectedMissionId,
  ]);

  const conversationGroups = useMemo(() => groupConversationsForSidebar(conversations), [conversations]);

  const handleReorderPinned = useCallback(
    async (nextVisibleOrderedIds: string[]) => {
      if (reorderingPinned) return;
      setReorderingPinned(true);
      try {
        await reorderPinnedConversations(nextVisibleOrderedIds);
      } finally {
        setReorderingPinned(false);
      }
    },
    [reorderPinnedConversations, reorderingPinned],
  );

  const handleMissionChange = useCallback(
    (missionId: string | null) => {
      setSelectedMissionId(missionId);
      writeLastMissionId(missionId);
      setExpandedGroups({});
      setArchivedListExpanded(false);
      if (isNewChat) {
        const mission = missions.find((item) => item.id === missionId);
        setActiveMission(missionId && mission ? u3204NormalizeMissionRef(mission) : null);
      }
    },
    [isNewChat, missions, setActiveMission, setSelectedMissionId],
  );

  const handleNewChat = async () => {
    if (selectedMissionId) {
      await createConversation(selectedMissionId);
    }
  };

  const handleRenameSave = async (title: string) => {
    if (!renameTarget?.version) return false;
    return renameConversation(renameTarget.id, renameTarget.version, title);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.version) return false;
    return deleteConversation(deleteTarget.id, deleteTarget.version);
  };

  const showMissionHint = selectedMissionId === null;

  const newChatDisabledTitle = !selectedMissionId
    ? !initLU.hasUserGroup
      ? t('emptyNoUserGroup', { ns: NS })
      : hasAccessibleMissions
        ? t('newChatRequiresMission', { ns: NS })
        : t('noAccessibleMissions', { ns: NS })
    : t('newChat', { ns: NS });

  if (collapsed) {
    return (
      <div className="flex w-12 flex-col items-center gap-2 border-r border-border bg-surface py-3">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
          title={t('showSidebar', { ns: NS })}
        >
          <FaBars />
        </button>
        <button
          type="button"
          onClick={() => void handleNewChat()}
          disabled={!selectedMissionId}
          className="rounded-lg p-2 text-primary hover:bg-muted disabled:opacity-50"
          title={newChatDisabledTitle}
        >
          <FaPlus />
        </button>
      </div>
    );
  }

  return (
    <aside className="flex min-h-0 w-72 shrink-0 flex-col border-r border-border bg-surface">
      <div className="space-y-3 border-b border-border p-3">
        <U3204MissionSelector
          missions={missions}
          selectedMissionId={selectedMissionId}
          onChange={handleMissionChange}
          hasUserGroup={initLU.hasUserGroup}
        />
        <button
          type="button"
          onClick={() => void handleNewChat()}
          disabled={!selectedMissionId}
          title={!selectedMissionId ? newChatDisabledTitle : undefined}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaPlus className="h-3 w-3" />
          {t('newChat', { ns: NS })}
        </button>
        <U3204ConversationFilterInput value={conversationFilterText} onChange={setConversationFilterText} />
        <div className="flex justify-end lg:hidden">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
            title={t('hideSidebar', { ns: NS })}
          >
            <FaBars />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
        {loadingConversations ? (
          <div className="flex justify-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">{t('noConversations', { ns: NS })}</p>
        ) : (
          <div className="space-y-4">
            {conversationGroups.map((group) => (
              <U3204ConversationGroupSection
                key={group.key}
                bucketLabelKey={group.labelKey}
                showMissionHint={showMissionHint}
                conversations={group.conversations}
                activeConversationId={activeConversationId}
                variant="active"
                expanded={expandedGroups[group.key] === true}
                enableReorder={group.key === 'pinned'}
                reordering={group.key === 'pinned' && reorderingPinned}
                onToggleExpanded={() =>
                  setExpandedGroups((current) => ({
                    ...current,
                    [group.key]: !current[group.key],
                  }))
                }
                onSelect={(id) => void loadConversation(id)}
                onRename={setRenameTarget}
                onPin={(c) => {
                  if (c.version != null) {
                    void pinConversation(c.id, c.version);
                  }
                }}
                onUnpin={(c) => {
                  if (c.version != null) {
                    void unpinConversation(c.id, c.version);
                  }
                }}
                onReorderPinned={(ids) => void handleReorderPinned(ids)}
                onArchive={(c) => {
                  if (c.version != null) {
                    void archiveConversation(c.id, c.version);
                  }
                }}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}

        <section className="mt-4 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setArchivesExpanded((expanded) => !expanded)}
            className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted"
          >
            {archivesExpanded ? <FaChevronDown className="h-3 w-3" /> : <FaChevronRight className="h-3 w-3" />}
            <span>{t('archives', { ns: NS })}</span>
            <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium normal-case">
              {archivedConversations.length}
            </span>
          </button>
          {archivesExpanded ? (
            <div className="mt-2">
              {loadingArchivedConversations ? (
                <div className="flex justify-center py-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : archivedConversations.length === 0 ? (
                <p className="px-2 py-2 text-center text-xs text-muted-foreground">
                  {t('noArchivedConversations', { ns: NS })}
                </p>
              ) : (
                <U3204ConversationGroupSection
                  showHeading={false}
                  showMissionHint={showMissionHint}
                  conversations={archivedConversations}
                  activeConversationId={activeConversationId}
                  variant="archived"
                  expanded={archivedListExpanded}
                  onToggleExpanded={() => setArchivedListExpanded((current) => !current)}
                  onSelect={(id) => void loadConversation(id)}
                  onUnarchive={(c) => {
                    if (c.version != null) {
                      void unarchiveConversation(c.id, c.version);
                    }
                  }}
                  onDelete={setDeleteTarget}
                />
              )}
            </div>
          ) : null}
        </section>
      </div>

      <U3204ConversationRenameDlg
        isOpen={renameTarget != null}
        initialTitle={renameTarget?.title ?? ''}
        onClose={() => setRenameTarget(null)}
        onSave={handleRenameSave}
      />

      <U3204ConversationDeleteDlg
        isOpen={deleteTarget != null}
        conversationTitle={deleteTarget?.title ?? null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </aside>
  );
};

export default U3204ConversationSidebar;
